import {
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import * as Yup from "yup";
import { useFormik } from "formik";
import React, { useEffect } from "react";
import { usePostLogInMutation } from "../../services/auth";
import { showError, showToast } from "../../constants/toasts";
import {
  getFromStorage,
  removeFromStorage,
  setToStorage,
} from "../../helpers/storage";
import { setCredentials } from "../../reducers/authSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/store";
import { STORAGE_KEYS } from "../../constants/storageKeys";
import Loader from "../../constants/Loader";
import { ROLES } from "../../utils/enums";

export const sidebar = [
  {
    label: "Dashboard",
    path: "/dashboard",
  },
  {
    label: "Manage Customers",
    path: "/manage-customers",
  },
  {
    label: "Manage Maintenance Team",
    path: "/manage-maintenance-team",
  },
  {
    label: "Manage Issues",
    path: "/manage-issues",
  },
  {
    label: "Manage Categories",
    path: "/manage-categories",
  },
  {
    label: "Manage Payments",
    path: "/manage-payments",
  },
  {
    label: "Manage Sub-Admin",
    path: "/manage-subadmin",
  },
  {
    label: "Revenue Analytics",
    path: "/revenue-analytics",
  },
  {
    label: "Manage Customer Support",
    path: "/manage-customer-support",
  },
  {
    label: "Notification Management",
    path: "/manage-notifications",
  },
  {
    label: "Manage Reviews",
    path: "/manage-reviews",
  },
  {
    label: "Manage CMS Pages",
    path: "/manage-cms",
  },
];

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loginMutation, { isLoading }] = usePostLogInMutation();

  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      remember: false,
    },
    validationSchema: Yup.object({
      remember: Yup.boolean(),
      email: Yup.string()
        .required("Email is required!")
        .matches(
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
          "Please enter a valid email address!"
        ),
      password: Yup.string()
        .required("Password is required")
        .min(6, "Password must be at least 6 characters"),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);
      const fcmToken = getFromStorage(STORAGE_KEYS.fcmToken);

      let body = {
        email: values.email,
        password: values.password,
        deviceToken: fcmToken||"123",
        deviceType: "WEB",
      };
      console.log(body, "dd");

      try {
        // let encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await loginMutation(body).unwrap();
        if (response?.statusCode === 200) {
          showToast("Logged In Successfully.");
          setToStorage(STORAGE_KEYS.token, response?.data?.token || "");
          dispatch(
            setCredentials({
              user: response?.data || null,
              token: response?.data?.token || "",
            })
          );

          if (formik.values.remember) {
            setToStorage(STORAGE_KEYS.credentials, JSON.stringify(body));
          } else {
            removeFromStorage(STORAGE_KEYS.credentials);
          }

          if (response?.data?.role === ROLES.SUB_ADMIN) {
            const permissions = response?.data?.accessRole?.permission;

            const firstPermissionLabel = permissions.find(
              (item: any) => item.isView === true
            ).label;
            const redirectPath = sidebar.find(
              (item) => item.label === firstPermissionLabel
            )?.path;

            if (redirectPath) {
              navigate(redirectPath);
              // showToast("Logged In Successfully.");
            } else {
              console.error("Invalid permission label for redirection");
            }
          } else {
            // showToast("Logged In Successfully.");
            navigate("/dashboard");
          }
        }
      } catch (error: any) {
        showError(error?.data?.message || "");
      }
    },
  });
  useEffect(() => {
    const data = getFromStorage(STORAGE_KEYS.credentials);
    if (data) {
      const rememberData = JSON.parse(`${data}`);
      formik.setFieldValue("password", rememberData?.password);
      formik.setFieldValue("email", rememberData?.email);
      formik.setFieldValue("remember", true);
    }
    // eslint-disable-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthLayout>
      <Loader isLoading={isLoading} />
      <div className="authBox">
        <h2>Login</h2>
        <form onSubmit={formik.handleSubmit}>
          <div className="control_group">
            <label>Email Address</label>
            <TextField
              className="text_field"
              placeholder="Enter Email Address"
              hiddenLabel
              fullWidth
              name="email"
              id="email"
              inputProps={{ maxLength: 80 }}
              value={formik.values.email}
              onChange={(val) => {
                if (val.target.value === " " || val.target.value === ".") {
                } else {
                  formik.handleChange(val);
                }
              }}
              onBlur={formik.handleBlur}
              helperText={formik.touched.email && formik.errors.email}
            />
          </div>
          <div className="control_group">
            <label>Password</label>
            <TextField
              className="text_field"
              placeholder="Enter Password"
              fullWidth
              hiddenLabel
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formik.values.password}
              onChange={(val) => {
                if (val.target.value === " " || val.target.value === ".") {
                } else {
                  formik.handleChange(val);
                }
              }}
              onBlur={formik.handleBlur}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div className="control_group d_flex">
            <div className="remember_box">
              <FormControlLabel
                control={<Checkbox />}
                name="remember"
                label="Remember me"
                checked={formik.values.remember}
                onChange={formik.handleChange}
              />
            </div>
            <a
              href="javacript:void(0)"
              className="anchor_link"
              onClick={() => navigate("/forgot-password")}
            >
              Forgot Password?
            </a>
          </div>

          <div className="form_btn">
            <Button className="btn btn_primary w_100" type="submit">
              Login
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Login;
